import { type ChangeEvent, type ComponentProps, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { DialogClose } from '@/components/ui/dialog';

type SubmissionFormData = {
  name: string;
  logo: string;
  image: string;
  logoFile: File | null;
  launchDate: string;
  endDate: string;
  description: string;
  url: string;
  gitRepoUrl: string;
};

type LogoAttachment = {
  content: string;
  name: string;
};

type BrevoMessageData = {
  sender: { email: string; name: string };
  to: { email: string }[];
  subject: string;
  textContent: string;
  attachment?: LogoAttachment[];
};

const INITIAL_FORM_DATA: SubmissionFormData = {
  name: '',
  logo: '',
  image: '',
  logoFile: null,
  launchDate: '',
  endDate: '',
  description: '',
  url: '',
  gitRepoUrl: '',
};

export const SubmissionForm = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogCloseRef = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();

  const isValidUrl = (value: string) => {
    if (!value) return true;
    try {
      const u = new URL(value);
      return !!u.protocol && !!u.host;
    } catch {
      return false;
    }
  };

  const getErrorMessage = (error: unknown) =>
    error instanceof Error ? error.message : 'Could not save submission. Please try again.';

  const hasRequiredFields = () => {
    if (formData.name && formData.launchDate && formData.endDate) {
      return true;
    }

    toast({
      title: 'Missing Information',
      description: 'Please fill in the software name, launch date, and end date.',
      variant: 'destructive',
    });

    return false;
  };

  const hasValidUrls = () => {
    if (isValidUrl(formData.url) && isValidUrl(formData.gitRepoUrl) && isValidUrl(formData.image)) {
      return true;
    }

    toast({
      title: 'Invalid URL',
      description: 'Please provide valid URLs for Website, Git repo, or Image.',
      variant: 'destructive',
    });

    return false;
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const closeDialogAfterSuccess = () => {
    if (!dialogCloseRef.current) {
      return;
    }

    setTimeout(() => {
      dialogCloseRef.current?.click();
    }, 1300);
  };

  const encodeLogoFile = async (): Promise<LogoAttachment | undefined> => {
    if (!formData.logoFile) {
      return undefined;
    }

    try {
      const arrayBuffer = await formData.logoFile.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';

      for (const element of bytes) {
        binary += String.fromCodePoint(element);
      }

      return {
        content: btoa(binary),
        name: formData.logoFile.name,
      };
    } catch {
      console.error('Something went wrong with the logo file.');
      toast({
        title: 'Error uploading logo',
        description: 'Something went wrong with the logo file. Please try again.',
      });
      return undefined;
    }
  };

  const buildEmailText = (logoAttachment?: LogoAttachment) => {
    return `
                      New Software Submission to GraveHub:
                      
                      Software Name: ${formData.name}
                      Logo/Emoji: ${formData.logo || '🪦'}
                      Image URL: ${formData.image || 'Not provided'}
                      Launch Date: ${formData.launchDate}
                      End Date: ${formData.endDate}
                      Description: ${formData.description || 'No description provided'}
                      Website URL: ${formData.url || 'Not provided'}
                      Git Repository URL: ${formData.gitRepoUrl || 'Not provided'}
                      Logo File: ${logoAttachment?.name || 'No file attached'}
          `.trim();
  };

  const sendBrevoEmail = async (logoAttachment?: LogoAttachment) => {
    const brevoApiKey = import.meta.env.VITE_BREVO_API_KEY;
    const brevoApiUrl = import.meta.env.VITE_BREVO_API_URL || 'https://api.brevo.com/v3/smtp/email';
    const brevoSenderName = import.meta.env.VITE_BREVO_SENDER_NAME || 'GraveHub';
    const brevoSenderEmail =
      import.meta.env.VITE_BREVO_SENDER_EMAIL || import.meta.env.VITE_MAIL_RECIPIENT;
    const mailRecipient = import.meta.env.VITE_MAIL_RECIPIENT;

    if (!brevoApiKey || !brevoApiUrl || !brevoSenderEmail || !mailRecipient) {
      console.warn('Brevo email configuration missing:', {
        hasApiKey: Boolean(brevoApiKey),
        hasApiUrl: Boolean(brevoApiUrl),
        hasSenderEmail: Boolean(brevoSenderEmail),
        hasRecipient: Boolean(mailRecipient),
      });
      return false;
    }

    const messageData: BrevoMessageData = {
      sender: {
        email: brevoSenderEmail,
        name: brevoSenderName,
      },
      to: [{ email: mailRecipient }],
      subject: `GraveHub Software: ${formData.name}`,
      textContent: buildEmailText(logoAttachment),
    };

    if (logoAttachment) {
      messageData.attachment = [logoAttachment];
    }

    try {
      const response = await fetch(brevoApiUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          'api-key': brevoApiKey,
        },
        body: JSON.stringify(messageData),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`Brevo request failed (${response.status}): ${errorBody}`);
        return false;
      }

      console.log('Email sent successfully via Brevo');
      return true;
    } catch (error) {
      console.error('Failed to send email via Brevo', error);
      return false;
    }
  };

  const submitForm = async () => {
    if (!hasRequiredFields() || !hasValidUrls()) {
      return;
    }

    try {
      const logoAttachment = await encodeLogoFile();
      const emailed = await sendBrevoEmail(logoAttachment);

      toast({
        title: 'Submission Saved',
        description: `Thank you for submitting ${formData.name} to the memorial.${emailed ? ' An email has been sent.' : ''}`,
      });

      if (emailed) {
        closeDialogAfterSuccess();
      }
    } catch (err: unknown) {
      console.error('Submission error', err);
      toast({
        title: 'Submission Error',
        description: getErrorMessage(err),
        variant: 'destructive',
      });
      return;
    }

    resetForm();
  };

  const handleSubmit: NonNullable<ComponentProps<'form'>['onSubmit']> = (e) => {
    e.preventDefault();
    void submitForm();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast({
          title: 'File too large',
          description: 'Logo file must be less than 5MB.',
          variant: 'destructive',
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an image file for the logo.',
          variant: 'destructive',
        });
        return;
      }

      setFormData((prev) => ({ ...prev, logoFile: file }));
    }
  };

  return (
    <Card className="bg-gradient-card border-memorial-border shadow-memorial">
      {/* Hidden DialogClose button for programmatic closing */}
      <DialogClose ref={dialogCloseRef} className="hidden" />
      <div className="p-8">
        <div className="mb-8 text-center">
          <h2 className="text-memorial-text mb-2 text-2xl font-bold">Submit to GraveHub</h2>
          <p className="text-memorial-muted">
            Know of software that should be remembered? Add it to our digital memorial.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-memorial-text">
                Software Name *
              </Label>
              <Input
                id="name"
                aria-label="Software Name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Internet Explorer"
                className="bg-memorial-bg border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo" className="text-memorial-text">
                Logo/Emoji
              </Label>
              <Input
                id="logo"
                aria-label="Software Logo"
                value={formData.logo}
                onChange={(e) => setFormData((prev) => ({ ...prev, logo: e.target.value }))}
                placeholder="🌐"
                className="bg-memorial-bg border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="logoFile" className="text-memorial-text">
              Logo Upload (Optional)
            </Label>
            <div className="space-y-2">
              <Input
                id="logoFile"
                aria-label="Software Logo Upload"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="bg-memorial-bg border-memorial-border text-memorial-text file:bg-cyber-glow/10 file:text-cyber-glow hover:file:bg-cyber-glow/20 file:mr-4 file:rounded file:border-0 file:px-4 file:py-2"
              />
              {formData.logoFile && (
                <p className="text-cyber-glow text-sm">Selected: {formData.logoFile.name}</p>
              )}
              <p className="text-memorial-muted text-xs">
                Upload an image file (max 5MB) or use emoji above
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="image" className="text-memorial-text">
              Image URL (Optional)
            </Label>
            <Input
              id="image"
              aria-label="Software Image URL"
              value={formData.image}
              onChange={(e) => setFormData((prev) => ({ ...prev, image: e.target.value }))}
              placeholder="https://example.com/image.png"
              className="bg-memorial-bg border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow"
            />
            <p className="text-memorial-muted text-xs">
              URL to an image (preferred over emoji/logo if provided)
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="launchDate" className="text-memorial-text">
                Launch Date *
              </Label>
              <Input
                id="launchDate"
                aria-label="Software Launch Date"
                type="date"
                value={formData.launchDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, launchDate: e.target.value }))}
                className="bg-memorial-bg border-memorial-border text-memorial-text focus:border-cyber-glow"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-memorial-text">
                Obsolete Date *
              </Label>
              <Input
                id="endDate"
                aria-label="Software Obsolete Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                className="bg-memorial-bg border-memorial-border text-memorial-text focus:border-cyber-glow"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="url" className="text-memorial-text">
                Website URL
              </Label>
              <Input
                id="url"
                aria-label="Software Website URL"
                value={formData.url}
                onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com"
                className="bg-memorial-bg border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gitRepoUrl" className="text-memorial-text">
                Git Repository URL
              </Label>
              <Input
                id="gitRepoUrl"
                aria-label="Software Git Repository URL"
                value={formData.gitRepoUrl}
                onChange={(e) => setFormData((prev) => ({ ...prev, gitRepoUrl: e.target.value }))}
                placeholder="https://github.com/user/repo"
                className="bg-memorial-bg border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-memorial-text">
              Description
            </Label>
            <Textarea
              id="description"
              aria-label="Software Description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of the software and its impact..."
              rows={3}
              className="bg-memorial-bg border-memorial-border text-memorial-text placeholder:text-memorial-muted focus:border-cyber-glow resize-none"
            />
          </div>

          <Button
            type="submit"
            className="bg-cyber-glow text-primary-foreground hover:bg-cyber-glow/80 hover:shadow-cyber w-full transition-all duration-300"
          >
            Send to GraveHub
          </Button>
        </form>
      </div>
    </Card>
  );
};
